import {
  Alert,
  Button,
  Card,
  Descriptions,
  Drawer,
  Empty,
  Form,
  InputNumber,
  Modal,
  message,
  Select,
  Space,
  Switch,
  Table,
  Tabs,
  Tag,
  Tooltip,
  Typography,
} from 'antd';
import type { ColumnsType } from 'antd/es/table';
import axios from 'axios';
import { Package, RefreshCw } from 'lucide-react';
import { observer } from 'mobx-react-lite';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { marketApi } from '@/services/marketApi';
import { brandStore } from '@/stores';
import type {
  MarketApplicationTarget,
  MarketAuditLog,
  MarketCategory,
  MarketItem,
  MarketListing,
  MarketRelease,
  MarketType,
  ProtoInt64,
} from '@/types';
import { isNextopBrand } from '@/utils/brand';
import { CategoryManager } from './CategoryManager';
import { findNewerAvailableRelease } from './versionAvailability';

const marketLabels: Record<MarketType, string> = {
  domestic: '国内',
  overseas: '海外',
};

const categoryLabels: Record<string, string> = {
  productivity: '生产力',
  communication: '沟通协作',
  'developer-tools': '开发者工具',
  creativity: '创意设计',
  'business-operations': '商业与运营',
  development: '开发者工具',
  other: '其他',
};

interface ListingFormValues {
  isListed: boolean;
  categoryId: string;
  categoryRank: number;
  featured: boolean;
  featuredRank: number;
}

interface ListingTarget {
  item: MarketItem;
  marketType: MarketType;
}

function categoryLabel(categoryId?: string) {
  return categoryLabels[categoryId ?? ''] ?? categoryId ?? '其他';
}

function parseObject(value?: string): Record<string, unknown> {
  try {
    const parsed: unknown = JSON.parse(value ?? '');
    return parsed && typeof parsed === 'object'
      ? (parsed as Record<string, unknown>)
      : {};
  } catch {
    return {};
  }
}

function nestedObject(value: unknown): Record<string, unknown> | undefined {
  return value && typeof value === 'object'
    ? (value as Record<string, unknown>)
    : undefined;
}

function manifestDisplay(item: MarketItem) {
  const manifest = parseObject(item.activeRelease?.manifestJson);
  const display = nestedObject(manifest.display) ?? {};
  return {
    name:
      typeof display.name === 'string' && display.name.trim()
        ? display.name
        : item.itemKey,
    description:
      typeof display.description === 'string' ? display.description : '',
    iconUrl: typeof display.iconUrl === 'string' ? display.iconUrl : '',
  };
}

function formatTime(value?: ProtoInt64) {
  const timestamp = Number(value);
  return Number.isFinite(timestamp) && timestamp > 0
    ? new Date(timestamp).toLocaleString('zh-CN')
    : '—';
}

function formatBytes(value: ProtoInt64) {
  const bytes = Number(value);
  if (!Number.isFinite(bytes) || bytes < 0) {
    return '—';
  }
  if (bytes < 1024) {
    return `${bytes} B`;
  }
  if (bytes < 1024 * 1024) {
    return `${(bytes / 1024).toFixed(1)} KiB`;
  }
  return `${(bytes / 1024 / 1024).toFixed(1)} MiB`;
}

function errorMessage(error: unknown, fallback: string) {
  if (axios.isAxiosError(error)) {
    const data = error.response?.data as
      | { message?: string; reason?: string }
      | undefined;
    return data?.message || data?.reason || error.message || fallback;
  }
  return error instanceof Error ? error.message : fallback;
}

function listingErrorMessage(error: unknown, marketType: MarketType) {
  if (axios.isAxiosError(error)) {
    const data = error.response?.data as
      | { message?: string; reason?: string }
      | undefined;
    if (data?.reason === 'MARKET_PRECONDITION_FAILED') {
      const detail = data.message?.toLowerCase() ?? '';
      if (detail.includes('does not support market')) {
        return `无法上架${marketLabels[marketType]}市场：当前应用服务仍在按旧版连接器市场声明校验，请升级后重试`;
      }
      if (detail.includes('select an active release')) {
        return `无法上架${marketLabels[marketType]}市场：请先通过“切换版本”选择当前版本`;
      }
      if (detail.includes('not available') || detail.includes('unavailable')) {
        return `无法上架${marketLabels[marketType]}市场：当前版本不可用，请先切换到状态为“可用”的版本`;
      }
      return `无法上架${marketLabels[marketType]}市场：当前版本缺少有效的运行实现，请重新发布该版本`;
    }
    if (data?.reason === 'MARKET_CONFLICT') {
      return '市场配置已被其他管理员更新，请刷新后再试';
    }
  }
  return errorMessage(error, '市场配置更新失败，请刷新后重试');
}

function listingOf(item: MarketItem, marketType: MarketType) {
  return item.listings.find(
    (listing: MarketListing) => listing.marketType === marketType,
  );
}

function itemIdentity(item: MarketItem) {
  return `${item.itemType}:${item.itemKey}`;
}

function ConnectorManifestOverview({ item }: { item: MarketItem }) {
  const manifest = parseObject(item.activeRelease?.manifestJson);
  const payload = nestedObject(manifest.payload) ?? {};
  const implementation = nestedObject(payload.implementation);
  const legacyImplementations = nestedObject(payload.implementations);
  const implementationKind =
    (typeof implementation?.kind === 'string' && implementation.kind) ||
    Object.values(legacyImplementations ?? {})
      .map((value) => nestedObject(value)?.kind)
      .find((value): value is string => typeof value === 'string') ||
    '—';
  if (!item.activeRelease) {
    return <Empty description="尚未选择当前版本" />;
  }
  return (
    <Space direction="vertical" size="middle" style={{ width: '100%' }}>
      <Descriptions bordered size="small" column={1}>
        <Descriptions.Item label="运行实现">
          {implementationKind}
        </Descriptions.Item>
        <Descriptions.Item label="上架范围">
          由下方国内、海外市场配置决定
        </Descriptions.Item>
      </Descriptions>
      <pre className="max-h-[440px] overflow-auto rounded-lg bg-gray-950 p-4 text-xs text-gray-100">
        {JSON.stringify(manifest, null, 2)}
      </pre>
    </Space>
  );
}

export const ConnectorManagementPage = observer(() => {
  const [listingForm] = Form.useForm<ListingFormValues>();
  const activeBrand = brandStore.activeBrand;
  const available = isNextopBrand(activeBrand);
  const marketTarget: MarketApplicationTarget = 'tutti-server';
  const [items, setItems] = useState<MarketItem[]>([]);
  const [loading, setLoading] = useState(false);
  const [actionKey, setActionKey] = useState('');
  const [detail, setDetail] = useState<MarketItem>();
  const [detailReleases, setDetailReleases] = useState<MarketRelease[]>([]);
  const [auditLogs, setAuditLogs] = useState<MarketAuditLog[]>([]);
  const [detailLoading, setDetailLoading] = useState(false);
  const [detailError, setDetailError] = useState('');
  const itemsRequestSequence = useRef(0);
  const detailRequestSequence = useRef(0);
  const [versionTarget, setVersionTarget] = useState<MarketItem>();
  const [versionReleases, setVersionReleases] = useState<MarketRelease[]>([]);
  const [selectedVersion, setSelectedVersion] = useState('');
  const [versionLoading, setVersionLoading] = useState(false);
  const versionRequestSequence = useRef(0);
  const [listingTarget, setListingTarget] = useState<ListingTarget>();
  const [listingCategories, setListingCategories] = useState<MarketCategory[]>(
    [],
  );
  const [listingCategoriesLoading, setListingCategoriesLoading] =
    useState(false);
  const [listingCategoriesError, setListingCategoriesError] = useState('');
  const listingRequestSequence = useRef(0);
  const featuredEnabled = Form.useWatch('featured', listingForm);

  const replaceItem = useCallback((next: MarketItem) => {
    setItems((current) =>
      current.map((item) =>
        itemIdentity(item) === itemIdentity(next)
          ? {
              ...next,
              latestAvailableRelease:
                next.latestAvailableRelease ?? item.latestAvailableRelease,
            }
          : item,
      ),
    );
    setDetail((current) =>
      current && itemIdentity(current) === itemIdentity(next)
        ? {
            ...next,
            latestAvailableRelease:
              next.latestAvailableRelease ?? current.latestAvailableRelease,
          }
        : current,
    );
  }, []);

  const loadItems = useCallback(async () => {
    const requestSequence = itemsRequestSequence.current + 1;
    itemsRequestSequence.current = requestSequence;
    if (!available) {
      setItems([]);
      return;
    }
    setLoading(true);
    try {
      const response = await marketApi.listItems(marketTarget, 'connector');
      if (itemsRequestSequence.current === requestSequence) {
        setItems(response.items ?? []);
      }
    } catch (error) {
      if (itemsRequestSequence.current === requestSequence) {
        message.error(errorMessage(error, '连接器加载失败'));
      }
    } finally {
      if (itemsRequestSequence.current === requestSequence) {
        setLoading(false);
      }
    }
  }, [available]);

  useEffect(() => {
    void loadItems();
  }, [loadItems]);

  const loadDetail = useCallback(async (item: MarketItem) => {
    const requestSequence = detailRequestSequence.current + 1;
    detailRequestSequence.current = requestSequence;
    setDetail(item);
    setDetailReleases([]);
    setAuditLogs([]);
    setDetailError('');
    setDetailLoading(true);
    try {
      const [releases, audit] = await Promise.all([
        marketApi.listReleases(marketTarget, item.itemType, item.itemKey),
        marketApi.listAuditLogs(marketTarget, item.itemType, item.itemKey),
      ]);
      if (detailRequestSequence.current !== requestSequence) {
        return;
      }
      setDetailReleases(releases.releases ?? []);
      setAuditLogs(audit.auditLogs ?? []);
    } catch (error) {
      if (detailRequestSequence.current !== requestSequence) {
        return;
      }
      const detailLoadError = errorMessage(error, '连接器详情加载失败');
      setDetailError(detailLoadError);
      message.error(detailLoadError);
    } finally {
      if (detailRequestSequence.current === requestSequence) {
        setDetailLoading(false);
      }
    }
  }, []);

  const closeDetail = useCallback(() => {
    detailRequestSequence.current += 1;
    setDetail(undefined);
    setDetailReleases([]);
    setAuditLogs([]);
    setDetailError('');
    setDetailLoading(false);
  }, []);

  const openVersionDialog = useCallback(
    async (item: MarketItem, preferredVersion?: string) => {
      const requestSequence = versionRequestSequence.current + 1;
      versionRequestSequence.current = requestSequence;
      setVersionTarget(item);
      setSelectedVersion(preferredVersion ?? item.activeVersion);
      setVersionLoading(true);
      try {
        const response = await marketApi.listReleases(
          marketTarget,
          item.itemType,
          item.itemKey,
        );
        if (versionRequestSequence.current === requestSequence) {
          setVersionReleases(response.releases ?? []);
        }
      } catch (error) {
        if (versionRequestSequence.current === requestSequence) {
          message.error(errorMessage(error, '版本列表加载失败'));
          setVersionTarget(undefined);
        }
      } finally {
        if (versionRequestSequence.current === requestSequence) {
          setVersionLoading(false);
        }
      }
    },
    [],
  );

  const closeVersionDialog = useCallback(() => {
    versionRequestSequence.current += 1;
    setVersionTarget(undefined);
    setVersionReleases([]);
    setSelectedVersion('');
    setVersionLoading(false);
  }, []);

  const confirmVersion = async () => {
    if (!versionTarget || !selectedVersion) {
      return;
    }
    setVersionLoading(true);
    try {
      const response = await marketApi.setActiveRelease(marketTarget, {
        itemType: versionTarget.itemType,
        itemKey: versionTarget.itemKey,
        version: selectedVersion,
        expectedLockVersion: versionTarget.lockVersion,
      });
      replaceItem(response.item);
      setVersionTarget(undefined);
      message.success('统一版本已更新，国内和海外市场同步生效');
      if (detail && itemIdentity(detail) === itemIdentity(response.item)) {
        await loadDetail(response.item);
      }
    } catch (error) {
      message.error(errorMessage(error, '版本切换失败，请刷新后重试'));
      await loadItems();
    } finally {
      setVersionLoading(false);
    }
  };

  const loadListingCategories = useCallback(
    async (item: MarketItem, marketType: MarketType) => {
      const requestSequence = listingRequestSequence.current + 1;
      listingRequestSequence.current = requestSequence;
      setListingCategories([]);
      setListingCategoriesError('');
      setListingCategoriesLoading(true);
      try {
        const response = await marketApi.listCategories(marketTarget, {
          itemType: item.itemType,
          marketType,
        });
        if (listingRequestSequence.current !== requestSequence) {
          return;
        }
        const categories = (response.categories ?? [])
          .filter((category) => category.kind === 'category')
          .sort((left, right) => left.sortOrder - right.sortOrder);
        if (categories.length === 0) {
          setListingCategoriesError('服务端没有返回可用分类');
          return;
        }
        setListingCategories(categories);
      } catch (error) {
        if (listingRequestSequence.current !== requestSequence) {
          return;
        }
        setListingCategoriesError(errorMessage(error, '分类加载失败'));
      } finally {
        if (listingRequestSequence.current === requestSequence) {
          setListingCategoriesLoading(false);
        }
      }
    },
    [],
  );

  const openListingDialog = useCallback(
    (item: MarketItem, marketType: MarketType) => {
      const listing = listingOf(item, marketType);
      setListingTarget({ item, marketType });
      listingForm.setFieldsValue({
        isListed: listing?.isListed ?? false,
        categoryId: listing?.categoryId || 'other',
        categoryRank: listing?.categoryRank ?? 0,
        featured: listing?.featured ?? false,
        featuredRank: listing?.featuredRank ?? 0,
      });
      void loadListingCategories(item, marketType);
    },
    [listingForm, loadListingCategories],
  );

  const closeListingDialog = useCallback(() => {
    listingRequestSequence.current += 1;
    setListingTarget(undefined);
    setListingCategories([]);
    setListingCategoriesError('');
    setListingCategoriesLoading(false);
    listingForm.resetFields();
  }, [listingForm]);

  const confirmListing = useCallback(async () => {
    if (!listingTarget) {
      return;
    }
    let values: ListingFormValues;
    try {
      values = await listingForm.validateFields();
    } catch {
      return;
    }
    const currentListing = listingOf(
      listingTarget.item,
      listingTarget.marketType,
    );
    const key = `${itemIdentity(listingTarget.item)}:${listingTarget.marketType}`;
    setActionKey(key);
    try {
      const response = await marketApi.setListing(marketTarget, {
        itemType: listingTarget.item.itemType,
        itemKey: listingTarget.item.itemKey,
        marketType: listingTarget.marketType,
        isListed: values.isListed,
        expectedIsListed: currentListing?.isListed ?? false,
        categoryId: values.categoryId,
        categoryRank: values.categoryRank,
        featured: values.featured,
        featuredRank: values.featured ? values.featuredRank : 0,
      });
      replaceItem(response.item);
      message.success(
        `${marketLabels[listingTarget.marketType]}市场配置已更新`,
      );
      closeListingDialog();
    } catch (error) {
      message.error(listingErrorMessage(error, listingTarget.marketType));
      await loadItems();
      closeListingDialog();
    } finally {
      setActionKey('');
    }
  }, [closeListingDialog, listingForm, listingTarget, loadItems, replaceItem]);

  const revokeRelease = useCallback(
    (release: MarketRelease) => {
      if (
        !detail ||
        itemIdentity(detail) !== `${release.itemType}:${release.itemKey}`
      ) {
        message.error('详情数据已失效，请重新打开后再操作');
        return;
      }
      Modal.confirm({
        title: `撤销 ${release.version}`,
        content: '撤销后该版本不能再设为当前版本，此操作会写入审计日志',
        okText: '确认撤销',
        okButtonProps: { danger: true },
        cancelText: '取消',
        onOk: async () => {
          const key = `${release.itemType}:${release.itemKey}:${release.version}:revoke`;
          setActionKey(key);
          try {
            await marketApi.revokeRelease(marketTarget, {
              itemType: release.itemType,
              itemKey: release.itemKey,
              version: release.version,
            });
            message.success('版本已撤销');
            if (detail) {
              await loadDetail(detail);
            }
            await loadItems();
          } catch (error) {
            message.error(errorMessage(error, '版本撤销失败'));
            throw error;
          } finally {
            setActionKey('');
          }
        },
      });
    },
    [detail, loadDetail, loadItems],
  );

  const deleteItem = useCallback(
    (item: MarketItem) => {
      Modal.confirm({
        title: `删除连接器 ${item.itemKey}`,
        content: (
          <Space direction="vertical" size={8}>
            <Typography.Text>
              该操作用于清理测试数据，删除后无法恢复。
            </Typography.Text>
            <Typography.Text type="danger">
              连接器的全部版本、国内外上架配置和审计记录都会被永久删除。
            </Typography.Text>
            <Typography.Text type="secondary">
              已上传的制品对象不由 Market 管理，不会随元数据一起删除。
            </Typography.Text>
          </Space>
        ),
        okText: '确认删除',
        okButtonProps: { danger: true },
        cancelText: '取消',
        onOk: async () => {
          const identity = itemIdentity(item);
          const key = `${identity}:delete`;
          setActionKey(key);
          try {
            await marketApi.deleteItem(marketTarget, {
              itemType: item.itemType,
              itemKey: item.itemKey,
              expectedLockVersion: item.lockVersion,
            });
            if (detail && itemIdentity(detail) === identity) {
              closeDetail();
            }
            setItems((current) =>
              current.filter(
                (candidate) => itemIdentity(candidate) !== identity,
              ),
            );
            message.success(`连接器 ${item.itemKey} 已删除`);
          } catch (error) {
            message.error(errorMessage(error, '连接器删除失败，请刷新后重试'));
            await loadItems();
          } finally {
            setActionKey('');
          }
        },
      });
    },
    [closeDetail, detail, loadItems],
  );

  const columns: ColumnsType<MarketItem> = useMemo(
    () => [
      {
        title: '连接器',
        key: 'item',
        render: (_, item) => {
          const display = manifestDisplay(item);
          return (
            <Space align="start">
              {display.iconUrl ? (
                <span className="flex h-10 w-10 shrink-0 overflow-hidden rounded-lg border border-border">
                  <img
                    alt=""
                    className="h-full w-full object-cover"
                    height={40}
                    src={display.iconUrl}
                    width={40}
                  />
                </span>
              ) : (
                <span className="flex h-10 w-10 shrink-0 items-center justify-center rounded-lg bg-gray-100">
                  <Package className="h-5 w-5 text-muted-foreground" />
                </span>
              )}
              <div>
                <Space>
                  <span className="font-medium">{display.name}</span>
                </Space>
                <div className="font-mono text-xs text-muted-foreground">
                  {item.itemKey}
                </div>
                {display.description ? (
                  <div className="mt-1 max-w-[320px] truncate text-xs text-muted-foreground">
                    {display.description}
                  </div>
                ) : null}
              </div>
            </Space>
          );
        },
      },
      {
        title: '统一版本',
        key: 'version',
        width: 180,
        render: (_, item) => {
          const newerRelease = findNewerAvailableRelease(item);
          return (
            <Space direction="vertical" size={2}>
              {item.activeVersion ? (
                <Tag color="blue">{item.activeVersion}</Tag>
              ) : (
                <Tag>未选择</Tag>
              )}
              {newerRelease ? (
                <Tag color="orange">新版本 {newerRelease.version} 可用</Tag>
              ) : null}
              <Button
                size="small"
                type="link"
                onClick={() =>
                  void openVersionDialog(item, newerRelease?.version)
                }
              >
                切换版本
              </Button>
            </Space>
          );
        },
      },
      ...(['domestic', 'overseas'] as MarketType[]).map((marketType) => ({
        title: `${marketLabels[marketType]}市场`,
        key: marketType,
        width: 190,
        render: (_: unknown, item: MarketItem) => {
          const listing = listingOf(item, marketType);
          const key = `${itemIdentity(item)}:${marketType}`;
          return (
            <Space direction="vertical" size={4}>
              <Space size={4} wrap>
                <Tag color={listing?.isListed ? 'green' : undefined}>
                  {listing?.isListed ? '已上架' : '已下架'}
                </Tag>
                <Tag>{categoryLabel(listing?.categoryId)}</Tag>
                {listing?.featured ? <Tag color="gold">精选</Tag> : null}
              </Space>
              <Button
                size="small"
                type="link"
                loading={actionKey === key}
                onClick={() => openListingDialog(item, marketType)}
              >
                配置
              </Button>
            </Space>
          );
        },
      })),
      {
        title: '来源',
        key: 'source',
        render: (_, item) => (
          <div className="max-w-[240px] font-mono text-xs">
            <div>{item.sourceRepository}</div>
            <div className="truncate text-muted-foreground">
              {item.sourcePath || '/'}
            </div>
          </div>
        ),
      },
      {
        title: '操作',
        key: 'actions',
        width: 140,
        render: (_, item) => (
          <Space size={0}>
            <Button type="link" onClick={() => void loadDetail(item)}>
              详情
            </Button>
            <Button
              danger
              type="link"
              loading={actionKey === `${itemIdentity(item)}:delete`}
              onClick={() => deleteItem(item)}
            >
              删除
            </Button>
          </Space>
        ),
      },
    ],
    [actionKey, deleteItem, loadDetail, openListingDialog, openVersionDialog],
  );

  const releaseColumns: ColumnsType<MarketRelease> = [
    {
      title: '版本',
      dataIndex: 'version',
      render: (version: string, release) => (
        <Space>
          <span className="font-mono">{version}</span>
          {detail?.activeVersion === version ? (
            <Tag color="blue">当前</Tag>
          ) : null}
          <Tag color={release.status === 'available' ? 'green' : 'red'}>
            {release.status === 'available' ? '可用' : '已撤销'}
          </Tag>
        </Space>
      ),
    },
    {
      title: '制品',
      key: 'artifact',
      render: (_, release) => (
        <Tooltip title={`${release.artifactKey}\n${release.artifactSha256}`}>
          <span className="font-mono text-xs">
            {formatBytes(release.artifactSizeBytes)} ·{' '}
            {release.artifactSha256.slice(0, 10)}
          </span>
        </Tooltip>
      ),
    },
    {
      title: '发布时间',
      dataIndex: 'publishedAtMs',
      render: formatTime,
    },
    {
      title: '操作',
      key: 'actions',
      render: (_, release) => (
        <Space>
          <Button
            size="small"
            disabled={
              !detail ||
              itemIdentity(detail) !==
                `${release.itemType}:${release.itemKey}` ||
              release.status !== 'available' ||
              detail?.activeVersion === release.version
            }
            onClick={() =>
              detail && void openVersionDialog(detail, release.version)
            }
          >
            设为当前
          </Button>
          <Button
            danger
            size="small"
            disabled={
              !detail ||
              itemIdentity(detail) !==
                `${release.itemType}:${release.itemKey}` ||
              release.status !== 'available' ||
              detail?.activeVersion === release.version
            }
            loading={
              actionKey ===
              `${release.itemType}:${release.itemKey}:${release.version}:revoke`
            }
            onClick={() => revokeRelease(release)}
          >
            撤销
          </Button>
        </Space>
      ),
    },
  ];

  const auditColumns: ColumnsType<MarketAuditLog> = [
    { title: '事件', dataIndex: 'eventType', width: 170 },
    {
      title: '市场',
      dataIndex: 'marketType',
      width: 90,
      render: (value: string) =>
        marketLabels[value as MarketType] ?? (value || '—'),
    },
    {
      title: '操作者',
      key: 'actor',
      width: 180,
      render: (_, entry) => `${entry.actorType}:${entry.actorId || '—'}`,
    },
    {
      title: '变更',
      dataIndex: 'changeJson',
      render: (value: string) => (
        <Tooltip title={value}>
          <span className="block max-w-[360px] truncate font-mono text-xs">
            {value || '—'}
          </span>
        </Tooltip>
      ),
    },
    {
      title: '时间',
      dataIndex: 'createdAtMs',
      width: 180,
      render: formatTime,
    },
  ];

  if (!available) {
    return (
      <Card>
        <Typography.Title level={3} style={{ marginTop: 0 }}>
          连接器管理
        </Typography.Title>
        <Empty description="连接器管理仅在 Tutti Brand 下可用，请先切换 Brand。" />
      </Card>
    );
  }

  return (
    <div className="space-y-6">
      <div className="flex flex-wrap items-start justify-between gap-4">
        <div>
          <Typography.Title level={3} style={{ margin: 0 }}>
            连接器管理
          </Typography.Title>
          <Typography.Text type="secondary">
            管理连接器版本、国内和海外上架状态及分类陈列
          </Typography.Text>
        </div>
        <Space>
          <CategoryManager key={marketTarget} target={marketTarget} />
          <Button
            icon={<RefreshCw className="h-4 w-4" />}
            loading={loading}
            onClick={() => void loadItems()}
          >
            刷新
          </Button>
        </Space>
      </div>

      <Alert
        showIcon
        type="info"
        message="连接器发布与上架"
        description="CI 发布不可变连接器版本；国内和海外独立管理上架与分类陈列，共享当前版本。"
      />

      <Card>
        <Table
          columns={columns}
          dataSource={items}
          loading={loading}
          locale={{ emptyText: <Empty description="CI 尚未发布连接器" /> }}
          pagination={{ pageSize: 20, showSizeChanger: false }}
          rowKey={itemIdentity}
          scroll={{ x: 1080 }}
        />
      </Card>

      <Modal
        title={
          versionTarget
            ? `切换连接器 / ${versionTarget.itemKey} 的统一版本`
            : ''
        }
        open={Boolean(versionTarget)}
        okText="确认切换"
        cancelText="取消"
        confirmLoading={versionLoading}
        okButtonProps={{ disabled: !selectedVersion }}
        onCancel={closeVersionDialog}
        onOk={() => void confirmVersion()}
      >
        <Alert
          className="mb-4"
          showIcon
          type="warning"
          message="已上架的国内和海外市场会同时使用新版本"
        />
        <Select
          className="w-full"
          loading={versionLoading}
          placeholder="选择可用版本"
          value={selectedVersion || undefined}
          options={versionReleases
            .filter((release) => release.status === 'available')
            .map((release) => ({
              value: release.version,
              label: `${release.version} · ${formatTime(release.publishedAtMs)}`,
            }))}
          onChange={setSelectedVersion}
        />
      </Modal>

      <Modal
        destroyOnHidden
        title={
          listingTarget
            ? `${marketLabels[listingTarget.marketType]}市场配置 / ${listingTarget.item.itemKey}`
            : ''
        }
        open={Boolean(listingTarget)}
        okText="保存配置"
        cancelText="取消"
        confirmLoading={
          listingTarget
            ? actionKey ===
              `${itemIdentity(listingTarget.item)}:${listingTarget.marketType}`
            : false
        }
        okButtonProps={{
          disabled:
            listingCategoriesLoading ||
            Boolean(listingCategoriesError) ||
            listingCategories.length === 0,
        }}
        onCancel={closeListingDialog}
        onOk={() => void confirmListing()}
      >
        <Alert
          className="mb-4"
          showIcon
          type="info"
          message="上架范围由管理端配置"
          description="连接器版本不再声明国内或海外范围。开启上架后，当前版本会出现在对应市场；两个市场可以分别配置。"
        />
        {listingCategoriesError ? (
          <Alert
            className="mb-4"
            showIcon
            type="error"
            message="服务端分类加载失败"
            description={listingCategoriesError}
            action={
              listingTarget ? (
                <Button
                  size="small"
                  onClick={() =>
                    void loadListingCategories(
                      listingTarget.item,
                      listingTarget.marketType,
                    )
                  }
                >
                  重试
                </Button>
              ) : null
            }
          />
        ) : null}
        <Form<ListingFormValues>
          form={listingForm}
          layout="vertical"
          preserve={false}
        >
          <Form.Item name="isListed" label="上架状态" valuePropName="checked">
            <Switch
              checkedChildren="已上架"
              unCheckedChildren="已下架"
              disabled={!listingTarget?.item.activeVersion}
            />
          </Form.Item>
          <Form.Item
            name="categoryId"
            label="分类"
            rules={[{ required: true, message: '请选择分类' }]}
          >
            <Select
              loading={listingCategoriesLoading}
              placeholder="从服务端分类中选择"
              options={listingCategories.map((category) => ({
                value: category.categoryId,
                label:
                  category.displayNameZh || categoryLabel(category.categoryId),
              }))}
            />
          </Form.Item>
          <Form.Item
            name="categoryRank"
            label="分类内排序"
            extra="数值越小越靠前；相同数值时按连接器标识稳定排序"
            rules={[{ required: true, message: '请填写分类内排序' }]}
          >
            <InputNumber className="w-full" min={0} precision={0} />
          </Form.Item>
          <Form.Item name="featured" label="精选" valuePropName="checked">
            <Switch checkedChildren="是" unCheckedChildren="否" />
          </Form.Item>
          <Form.Item
            name="featuredRank"
            label="精选排序"
            extra="仅在开启精选时生效，数值越小越靠前"
            rules={
              featuredEnabled
                ? [{ required: true, message: '请填写精选排序' }]
                : undefined
            }
          >
            <InputNumber
              className="w-full"
              min={0}
              precision={0}
              disabled={!featuredEnabled}
            />
          </Form.Item>
        </Form>
      </Modal>

      <Drawer
        destroyOnHidden
        title={detail ? `连接器 / ${detail.itemKey}` : ''}
        open={Boolean(detail)}
        width={960}
        onClose={closeDetail}
      >
        {detail ? (
          <Space direction="vertical" size="large" style={{ width: '100%' }}>
            {detailError ? (
              <Alert
                showIcon
                type="error"
                message="详情加载失败"
                description={detailError}
                action={
                  <Button size="small" onClick={() => void loadDetail(detail)}>
                    重试
                  </Button>
                }
              />
            ) : null}
            <Descriptions bordered size="small" column={2}>
              <Descriptions.Item label="统一版本">
                {detail.activeVersion || '未选择'}
              </Descriptions.Item>
              <Descriptions.Item label="乐观锁版本">
                {detail.lockVersion}
              </Descriptions.Item>
              <Descriptions.Item label="国内市场">
                <Space size={4} wrap>
                  <Tag
                    color={
                      listingOf(detail, 'domestic')?.isListed
                        ? 'green'
                        : undefined
                    }
                  >
                    {listingOf(detail, 'domestic')?.isListed
                      ? '已上架'
                      : '已下架'}
                  </Tag>
                  <Tag>
                    {categoryLabel(listingOf(detail, 'domestic')?.categoryId)}
                  </Tag>
                  {listingOf(detail, 'domestic')?.featured ? (
                    <Tag color="gold">精选</Tag>
                  ) : null}
                </Space>
              </Descriptions.Item>
              <Descriptions.Item label="海外市场">
                <Space size={4} wrap>
                  <Tag
                    color={
                      listingOf(detail, 'overseas')?.isListed
                        ? 'green'
                        : undefined
                    }
                  >
                    {listingOf(detail, 'overseas')?.isListed
                      ? '已上架'
                      : '已下架'}
                  </Tag>
                  <Tag>
                    {categoryLabel(listingOf(detail, 'overseas')?.categoryId)}
                  </Tag>
                  {listingOf(detail, 'overseas')?.featured ? (
                    <Tag color="gold">精选</Tag>
                  ) : null}
                </Space>
              </Descriptions.Item>
            </Descriptions>
            <Tabs
              items={[
                {
                  key: 'releases',
                  label: `版本（${detailReleases.length}）`,
                  children: (
                    <Table
                      columns={releaseColumns}
                      dataSource={detailReleases}
                      loading={detailLoading}
                      pagination={false}
                      rowKey={(release) => String(release.releaseId)}
                    />
                  ),
                },
                {
                  key: 'manifest',
                  label: '当前 Manifest',
                  children: <ConnectorManifestOverview item={detail} />,
                },
                {
                  key: 'audit',
                  label: `审计日志（${auditLogs.length}）`,
                  children: (
                    <Table
                      columns={auditColumns}
                      dataSource={auditLogs}
                      loading={detailLoading}
                      pagination={false}
                      rowKey={(entry) => String(entry.auditId)}
                      scroll={{ x: 820 }}
                    />
                  ),
                },
              ]}
            />
          </Space>
        ) : null}
      </Drawer>
    </div>
  );
});
